// Rasterize the actual flight workspace, preserving its DOM, styles and charts.
// The live page stays untouched; dialogs and the fleet sidebar are not captured.
export async function createSharePreview(document: Document): Promise<string | null> {
  const root = document.querySelector<HTMLElement>("main.workspace");
  const view = document.defaultView;
  if (!root || !view) return null;
  await document.fonts.ready;
  const bounds = root.getBoundingClientRect();
  if (!bounds.width || !bounds.height) return null;
  const chart = Array.from(root.querySelectorAll("canvas"))
    .find((node) => node.getBoundingClientRect().width > 400 && node.getBoundingClientRect().height > 150);
  const width = Math.ceil(bounds.width);
  const height = Math.ceil(Math.min(root.scrollHeight,
    Math.max(view.innerHeight, chart ? chart.getBoundingClientRect().bottom - bounds.top + 20 : 0), width * 2));
  const resources = new Map<string, Promise<string>>();
  const embed = (url: string): Promise<string> => {
    if (url.startsWith("data:") || url.startsWith("#")) return Promise.resolve(url);
    const absolute = new URL(url, document.baseURI).href;
    if (!resources.has(absolute)) resources.set(absolute, (async () => {
      const response = await fetch(absolute, { credentials: "same-origin" });
      if (!response.ok) throw new Error("Could not load an image for the page screenshot.");
      const blob = await response.blob();
      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(new Error("Could not read a screenshot image."));
        reader.readAsDataURL(blob);
      });
    })());
    return resources.get(absolute)!;
  };
  const embedUrls = async (value: string) => {
    const matches = Array.from(value.matchAll(/url\(["']?([^"')]+)["']?\)/g));
    for (const match of matches) value = value.replace(match[0], `url("${await embed(match[1])}")`);
    return value;
  };
  const copyStyle = async (style: CSSStyleDeclaration, target: CSSStyleDeclaration) => {
    for (const property of Array.from(style)) {
      const value = style.getPropertyValue(property);
      target.setProperty(property, value.includes("url(") ? await embedUrls(value) : value);
    }
    target.setProperty("animation", "none");
    target.setProperty("transition", "none");
    target.setProperty("caret-color", "transparent");
  };
  const pseudoRules: string[] = [];
  let nextId = 0;
  const cloneNode = async (node: Node): Promise<Node | null> => {
    if (node.nodeType === Node.TEXT_NODE) return node.cloneNode();
    if (!(node instanceof Element) || node.matches("dialog, script, style, link, iframe, [role=tooltip], #fleetSidebar")) return null;
    const computed = view.getComputedStyle(node);
    if (computed.display === "none" || computed.visibility === "hidden") return null;
    let clone: Element;
    if (node instanceof HTMLCanvasElement) {
      const image = document.createElement("img");
      image.src = node.toDataURL("image/png");
      clone = image;
    } else {
      clone = node.cloneNode(false) as Element;
      for (const attribute of Array.from(clone.attributes)) {
        if (attribute.name.startsWith("on")) clone.removeAttribute(attribute.name);
      }
      if (node instanceof HTMLImageElement) {
        clone.setAttribute("src", await embed(node.currentSrc || node.src));
        clone.removeAttribute("srcset");
        clone.removeAttribute("loading");
      }
      for (const child of Array.from(node.childNodes)) {
        const copy = await cloneNode(child);
        if (copy) clone.append(copy);
      }
      if (node instanceof HTMLInputElement) clone.setAttribute("value", node.value);
      if (node instanceof HTMLTextAreaElement) clone.textContent = node.value;
      if (node instanceof HTMLSelectElement) {
        Array.from(clone.querySelectorAll("option")).forEach((option, index) => option.toggleAttribute("selected", node.options[index].selected));
      }
    }
    await copyStyle(computed, (clone as HTMLElement | SVGElement).style);
    for (const pseudo of ["::before", "::after"]) {
      const style = view.getComputedStyle(node, pseudo);
      if (!style.content || style.content === "none" || style.content === "normal") continue;
      const id = `screenshot-${nextId++}`;
      clone.classList.add(id);
      const holder = document.createElement("span");
      await copyStyle(style, holder.style);
      pseudoRules.push(`.${id}${pseudo}{${holder.style.cssText}}`);
    }
    return clone;
  };
  const clone = await cloneNode(root) as HTMLElement;
  clone.style.margin = "0";
  clone.style.position = "relative";
  clone.style.inset = "auto";
  clone.style.transform = "none";
  clone.style.width = `${width}px`;
  clone.style.boxSizing = "border-box";
  const wrapper = document.createElement("div");
  wrapper.setAttribute("xmlns", "http://www.w3.org/1999/xhtml");
  wrapper.style.width = `${width}px`;
  wrapper.style.height = `${height}px`;
  wrapper.style.overflow = "hidden";
  wrapper.style.background = view.getComputedStyle(document.body).backgroundColor;
  const style = document.createElement("style");
  style.textContent = pseudoRules.join("\n");
  wrapper.append(style, clone);
  const html = new XMLSerializer().serializeToString(wrapper);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}"><foreignObject width="100%" height="100%">${html}</foreignObject></svg>`;
  const image = new Image();
  image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  await image.decode();
  const scale = Math.min(1, 1920 / width, 1920 / height);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Page screenshots are unavailable in this browser.");
  // Keep dense pages within the endpoint's 2 MiB PNG limit without cropping.
  for (;;) {
    context.fillStyle = "#0b1118";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const png = canvas.toDataURL("image/png");
    if ((png.length - 22) * 3 / 4 <= 2 * 1024 * 1024) return png;
    canvas.width = Math.max(1, Math.floor(canvas.width * 0.85));
    canvas.height = Math.max(1, Math.floor(canvas.height * 0.85));
  }
}
